import React, { memo, useCallback, useRef, useState, ReactNode } from "react";

//styles
import { StyledWrapper } from "./styles";

//utils
import { useEffectOnce } from "@swiggy-private/core-ui";

export interface ResizeTransitionContainerProps {
    children: ReactNode;
    transitionTime?: number;
}
const ResizeTransitionContainer: React.FC<ResizeTransitionContainerProps> = props => {
    const [dynamicHeight, setDynamicHeight] = useState<number | "auto">("auto");
    const contentRef = useRef<HTMLDivElement>(null);
    const resizeObserver = useRef<ResizeObserver | null>(null);
    const mutationObserver = useRef<MutationObserver | null>(null);

    const updateHeight = useCallback(() => {
        if (contentRef.current) {
            const contentHeight = contentRef.current.scrollHeight;
            if (contentHeight !== dynamicHeight) {
                // Using double requestAnimationFrame to ensure smoother height transition on Android devices
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        setDynamicHeight(contentHeight);
                    });
                });
            }
        }
    }, [dynamicHeight]);

    // Update height on content change
    useEffectOnce(() => {
        updateHeight();

        if (typeof window !== "undefined" && !resizeObserver.current && !mutationObserver.current) {
            if (typeof window.ResizeObserver !== "undefined") {
                resizeObserver.current = new ResizeObserver(updateHeight);

                if (contentRef.current) {
                    resizeObserver.current.observe(contentRef.current);
                }

                // Cleanup the observer on unmount
                return () => resizeObserver.current?.disconnect();
            } else if (typeof MutationObserver !== "undefined") {
                // Fallback for browsers that don't support ResizeObserver
                mutationObserver.current = new MutationObserver(updateHeight);

                if (contentRef.current) {
                    mutationObserver.current.observe(contentRef.current, {
                        childList: true,
                        subtree: true,
                    });
                }

                // Cleanup the observer on unmount
                return () => mutationObserver.current?.disconnect();
            }
        }
    });

    return (
        <StyledWrapper dynamicHeight={dynamicHeight} transitionTime={props.transitionTime}>
            <div ref={contentRef}>{props.children}</div>
        </StyledWrapper>
    );
};

export default memo(ResizeTransitionContainer);
